import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

/**
 * BuildVersionBadge
 *
 * A small pill that shows the *currently loaded* build version (the one
 * baked into this JS bundle at build time) alongside the *server's
 * latest* build version (fetched from /build-version.json).
 *
 * When the two diverge, the badge turns amber and a "Force Refresh"
 * button appears.  Tapping it:
 *   1. Unregisters all service workers (kills any sticky SW from a
 *      previous deploy).
 *   2. Clears every CacheStorage cache.
 *   3. Reloads the page with a unique cache-busting query string so
 *      Chrome's HTTP cache cannot serve a stale index.html or JS bundle.
 *
 * This is the *only* reliable way to recover from an Android TWA whose
 * Chrome WebView has aggressively cached an old bundle and ignores
 * `registration.update()`.
 *
 * Place this badge in the user menu and (optionally) the Settings page.
 */
export const forceAppRefresh = async () => {
  try {
    toast.loading('Clearing cache & refreshing…', { id: 'force-refresh' });
    // 1. Tell every controlling SW to skip waiting (in case there's a
    //    new SW stuck in the "waiting" state).
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        try {
          if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          // Ask the active SW to nuke every cache it knows about.
          if (reg.active) reg.active.postMessage({ type: 'CLEAR_ALL_CACHES' });
          // Then unregister so the next page load starts fresh.
          await reg.unregister();
        } catch (_) { /* keep going */ }
      }
    }
    // 2. Nuke CacheStorage from the page side too (in case the SW
    //    didn't get a chance to do it).
    if ('caches' in window) {
      try {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      } catch (_) { /* ignore */ }
    }
    // 3. Hard reload with a unique query string so Chrome's HTTP cache
    //    can't serve a stale index.html.  We replace the URL so the
    //    user's history isn't polluted with `?_refresh=…` entries.
    const sep = window.location.search ? '&' : '?';
    const url = `${window.location.pathname}${window.location.search}${sep}_refresh=${Date.now()}${window.location.hash}`;
    window.location.replace(url);
  } catch (e) {
    toast.error('Force refresh failed: ' + (e?.message || e), { id: 'force-refresh' });
  }
};

export function BuildVersionBadge({ compact = false }) {
  // window.__BBC_BUILD_VERSION__ is replaced at build time by
  // scripts/post-build.js with the build timestamp.  Falls back to '0'
  // so the badge still renders during local development.
  const localVersion =
    (typeof window !== 'undefined' && window.__BBC_BUILD_VERSION__) || '0';
  const [serverVersion, setServerVersion] = useState(null);
  const [checking, setChecking] = useState(false);

  const checkRemote = async () => {
    try {
      setChecking(true);
      // Cache-busting query string — Chrome MUST hit the network.
      const r = await fetch(`/build-version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setServerVersion(j.version || null);
    } catch (e) {
      console.warn('[Version] Failed to fetch build-version.json:', e);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    checkRemote();
    // Recheck every 5 minutes — quick enough to surface deploys, not
    // chatty enough to be a nuisance.
    const t = setInterval(checkRemote, 5 * 60 * 1000);
    // Also recheck when the tab becomes visible (covers Android TWA
    // returning from background).
    const onVis = () => { if (document.visibilityState === 'visible') checkRemote(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const isStale = serverVersion && serverVersion !== localVersion && localVersion !== '0';

  const forceRefresh = forceAppRefresh;

  if (compact) {
    return (
      <button
        type="button"
        onClick={isStale ? forceRefresh : checkRemote}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono transition-colors ${
          isStale ? 'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
        }`}
        title={isStale ? 'New version available — tap to refresh' : 'Build version'}
        data-testid="build-version-badge"
      >
        <RefreshCw className={`w-3 h-3 ${checking ? 'animate-spin' : ''}`} />
        v{(localVersion || '0').slice(-6)}
        {isStale && <span className="ml-0.5">!</span>}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 text-xs" data-testid="build-version-info">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Build:</span>
        <code className="font-mono text-[11px]">{localVersion}</code>
        {isStale && (
          <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium">
            Update available
          </span>
        )}
      </div>
      {serverVersion && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Latest:</span>
          <code className="font-mono text-[11px]">{serverVersion}</code>
        </div>
      )}
      <button
        type="button"
        onClick={forceRefresh}
        className="mt-1 inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-md bg-forest-600 text-white text-xs hover:bg-forest-700 transition-colors"
        data-testid="force-refresh-btn"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Force update (clear cache & reload)
      </button>
    </div>
  );
}
